import { usePageMeta } from "@/lib/use-page-meta";
import { createFileRoute, Link, useNavigate, ClientOnly } from "@tanstack/react-router";
import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useEffect, Suspense } from "react";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { MapView, type MapMode } from "@/components/map-view";
import { CountryLabel } from "@/components/country-label";
import { TopCountriesPanel } from "@/components/top-countries-panel";
import { SuggestYoutuberDialog } from "@/components/suggest-youtuber-dialog";
import {
  youtubersListQuery,
  recentVideosQuery,
  aggregatesQuery,
  allVideosQuery,
  mapPinsQuery,
  creatorsListQuery,
  primaryLocation,
  creatorStatsFrom,
  countriesFrom,
  videoParam,
  siteStatsQuery,
} from "@/lib/queries";
import {
  formatCompact,
  formatDate,
  formatNumber,
  translateCountry,
  cleanTitle,
} from "@/lib/format";
import { safeLoadAll } from "@/lib/ssr-safe";
import "@/i18n";

export const Route = createFileRoute("/")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Youtubers Map — Atles de youtubers viatgers" },
      { property: "og:title", content: "Youtubers Map — Atles de youtubers viatgers" },
      { property: "og:description", content: "Un atles viu dels youtubers viatgers. Explora vídeos geolocalitzats en un mapa interactiu." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "description",
        content:
          "Un atles viu dels youtubers viatgers. Explora vídeos geolocalitzats en un mapa interactiu.",
      },
    ],
  }),
  // Await only the small, SEO-relevant reads so the server responds fast;
  // the heavy map/aggregate data is prefetched and streamed to the client.
  loader: async ({ context }) => {
    void context.queryClient.prefetchQuery(youtubersListQuery);
    // Cheap aggregate read (instant database function): the figures band is
    // server-rendered, so the loader waits for it instead of racing the render.
    await safeLoadAll([
      ["home:siteStats", context.queryClient.ensureQueryData(siteStatsQuery)],
      ["home:creators", context.queryClient.ensureQueryData(creatorsListQuery)],
      ["home:recentVideos", context.queryClient.ensureQueryData(recentVideosQuery)],
    ]);
  },
  component: HomePage,
});

function HomePage() {
  return (
    <div className="min-h-screen">
      <SiteNav />
      {/* Heading is server-rendered outside ClientOnly so the LCP text node is
          painted immediately and never replaced on hydration. */}
      <HeroHeader />
      <ClientOnly fallback={<HeroSkeleton />}>
        <Suspense fallback={<HeroSkeleton />}>
          <HeroMap />
        </Suspense>
      </ClientOnly>
      <ArchiveStats />
      <Suspense fallback={<Loading />}>
        <FeaturedYoutubers />
      </Suspense>
      <Suspense fallback={<Loading />}>
        <RecentVideos />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function HeroHeader() {
  const { t } = useTranslation();
  usePageMeta(t("meta.homeTitle"), t("meta.homeDesc"));
  return (
    <div className="max-w-7xl mx-auto px-6 pt-5">
      <div className="mb-3">
        <h1 className="font-serif text-xl md:text-2xl leading-tight">{t("home.tagline")}</h1>
        <p className="text-sm text-ink/60 mt-1 max-w-2xl">{t("home.subtitle")}</p>
      </div>
    </div>
  );
}

/** Same box model as <HeroMap /> so hydration causes no layout shift. */
function HeroSkeleton() {
  return (
    <section className="max-w-7xl mx-auto px-6 pb-6" aria-busy="true">

      <div className="grid grid-cols-2 gap-2 items-center mb-3 md:flex md:flex-wrap md:gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="ring-hairline bg-paper/60 h-[27px] w-full md:w-28 animate-pulse" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 h-[32vh] min-h-[200px] md:h-[55vh] md:min-h-[380px] ring-hairline bg-secondary overflow-hidden animate-pulse" />
        <div className="lg:col-span-2 ring-hairline bg-paper/50 h-[32vh] min-h-[200px] md:h-[55vh] md:min-h-[380px] animate-pulse" />
      </div>
    </section>
  );
}

function Loading() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-24 text-center text-mono-caps text-ink/40">…</div>
  );
}


function HeroMap() {
  const { t, i18n } = useTranslation();
  const { data: videos } = useSuspenseQuery(mapPinsQuery);
  const { data: creators } = useSuspenseQuery(creatorsListQuery);

  const [mode, setMode] = useState<MapMode>("heatmap");
  const [country, setCountry] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [creatorId, setCreatorId] = useState<string>("");

  const years = useMemo(() => {
    const s = new Set<number>();
    for (const v of videos) if (v.year) s.add(v.year);
    return Array.from(s).sort((a, b) => b - a);
  }, [videos]);

  const filtered = useMemo(() => {
    return videos.filter((v) => {
      if (!v.cc) return false;
      if (country && v.cc !== country) return false;
      if (creatorId && v.creator_id !== creatorId) return false;
      if (year && String(v.year ?? "") !== year) return false;
      return true;
    });
  }, [videos, country, creatorId, year]);

  const pins = useMemo(
    () =>
      filtered
        .filter((v) => v.lat && v.lng)
        .map((v) => ({
          id: v.id,
          lat: v.lat as number,
          lng: v.lng as number,
          color: "oklch(0.62 0.15 55)",
          label: cleanTitle(v.title),
          subtitle: [v.city, translateCountry(v.cc, v.cn, i18n.language)].filter(Boolean).join(", ") || undefined,
          thumbnail: `https://i.ytimg.com/vi/${v.ytId}/mqdefault.jpg`,
          href: `/v/${v.slug || v.id}`,
          countryCode: v.cc,
        })),
    [filtered, i18n.language],
  );

  const navigate = useNavigate();

  const countryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of filtered) {
      const k = v.cc!.toUpperCase();
      counts[k] = (counts[k] ?? 0) + 1;
    }
    return counts;
  }, [filtered]);

  const countriesOptions = useMemo(() => {
    const rows = countriesFrom(
      videos.map((v) => ({
        id: v.id,
        youtuber_id: v.youtuber_id,
        view_count: v.views,
        video_locations: v.cc ? [{ country_code: v.cc, country_name: v.cn, is_primary: true }] : [],
      })),
    );
    return rows.sort((a, b) =>
      translateCountry(a.code, a.name, i18n.language).localeCompare(
        translateCountry(b.code, b.name, i18n.language),
      ),
    );
  }, [videos, i18n.language]);

  return (
    <section className="max-w-7xl mx-auto px-6 pb-6">


      {/* Filters + mode toggle */}
      {/* Two balanced rows on mobile (2x2), one line from md up. */}
      <div className="grid grid-cols-2 gap-2 items-center mb-3 md:flex md:flex-wrap md:gap-2">
        <select
          aria-label={t("home.allCountries")}
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="bg-paper ring-hairline min-w-0 text-xs px-2.5 py-1.5 hover:bg-secondary transition-colors cursor-pointer"
        >
          <option value="">{t("home.allCountries")}</option>
          {countriesOptions.map((c) => (
            <option key={c.code} value={c.code}>
              {translateCountry(c.code, c.name, i18n.language)} ({c.videos})
            </option>
          ))}
        </select>
        <select
          aria-label={t("home.allYears")}
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="bg-paper ring-hairline min-w-0 text-xs px-2.5 py-1.5 hover:bg-secondary transition-colors cursor-pointer"
        >
          <option value="">{t("home.allYears")}</option>
          {years.map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
        <select
          aria-label={t("home.allYoutubers")}
          value={creatorId}
          onChange={(e) => setCreatorId(e.target.value)}
          className="bg-paper ring-hairline min-w-0 text-xs px-2.5 py-1.5 hover:bg-secondary transition-colors cursor-pointer"
        >
          <option value="">{t("home.allYoutubers")}</option>
          {creators.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          aria-label={t("home.mapMode")}
          value={mode}
          onChange={(e) => setMode(e.target.value as MapMode)}
          className="bg-paper ring-hairline min-w-0 text-xs px-2.5 py-1.5 hover:bg-secondary transition-colors cursor-pointer"
        >
          <option value="points">{t("home.mapPoints")}</option>
          <option value="heatmap">{t("home.mapHeatmap")}</option>
          <option value="countries">{t("home.mapCountries")}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 h-[32vh] min-h-[200px] md:h-[55vh] md:min-h-[380px] ring-hairline bg-secondary overflow-hidden">
          <MapView
            pins={pins}
            mode={mode}
            countryCounts={countryCounts}
            onCountryClick={(code) => navigate({ to: "/country/$code", params: { code: code.toLowerCase() } })}
            className="w-full h-full"
            fitToPins={mode !== "countries" && pins.length > 0}
            interactive
          />
        </div>
        <div className="lg:col-span-2">
          <TopCountriesPanel />
        </div>
      </div>
    </section>
  );
}

function FeaturedYoutubers() {
  const { t } = useTranslation();
  const { data: creators } = useSuspenseQuery(creatorsListQuery);
  // Per-creator counters come from a large aggregate read: fetch it after the
  // page is already visible instead of blocking the server response.
  const { data: agg } = useQuery(aggregatesQuery);
  // The counters only render once we are on the client, so the first client
  // render matches the server HTML exactly (no hydration mismatch).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const stats = useMemo(
    () => (agg ? creatorStatsFrom(agg, creators) : new Map<string, { videos: number; countries: number }>()),
    [agg, creators],
  );

  const featured = useMemo(() => {
    return creators
      .map((c) => {
        const primary = c.channels.find((ch) => ch.is_primary_channel) ?? c.channels[0];
        const maxSubs = c.channels.reduce((m, ch) => Math.max(m, ch.subscriber_count ?? 0), 0);
        return {
          id: c.id,
          slug: c.slug,
          name: c.name,
          avatar_url: c.avatar_url ?? primary?.avatar_url ?? null,
          country: c.country ?? primary?.country ?? null,
          subscriber_count: maxSubs,
        };
      })
      .sort((a, b) => b.subscriber_count - a.subscriber_count)
      .slice(0, 11);
  }, [creators]);

  if (featured.length === 0) {
    return (
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center text-ink/50">
          <p className="text-mono-caps text-amber mb-3">{t("home.noCreatorsKicker")}</p>
          <p>{t("home.noCreatorsBody")}</p>
        </div>
      </section>
    );
  }
  return (
    <section className="max-w-7xl mx-auto px-6 py-8 border-t border-ink/8">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="text-xl md:text-2xl">{t("home.featured")}</h2>
          
        </div>
        <Link
          to="/youtubers"
          className="text-sm font-medium border-b border-ink/20 pb-0.5 hover:border-amber transition-colors"
        >
          {t("home.viewDirectory")}
        </Link>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {featured.map((y) => {
          const s = stats.get(y.id) ?? { videos: 0, countries: 0 };
          return (
            <Link key={y.id} to="/y/$handle" params={{ handle: y.slug }} className="group">
              <div className="aspect-square bg-secondary overflow-hidden ring-hairline mb-1.5 group-hover:ring-amber/40 transition-all">
                {y.avatar_url ? (
                  <img
                    src={y.avatar_url.replace(/=s\d+(-[^/]*)?$/, "=s176")}
                    alt={y.name}
                    loading="lazy"
                    decoding="async"
                    width={176}
                    height={176}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-mono-caps text-ink/30">
                    {y.name.slice(0, 2)}
                  </div>
                )}
              </div>
              <h3 className="font-serif text-xs leading-tight truncate group-hover:text-amber transition-colors flex items-center gap-1.5">
                {y.country && (
                  <img
                    src={`https://flagcdn.com/w20/${y.country.toLowerCase()}.png`}
                    alt=""
                    width={14}
                    height={10}
                    className="inline-block flex-shrink-0 ring-1 ring-ink/10"
                  />
                )}
                <span className="truncate">{y.name}</span>
              </h3>
              <div className="mt-0.5 font-mono text-[10px] text-ink/50 leading-tight">
                <div>{formatCompact(y.subscriber_count)} subs</div>
                <div>
                  {hydrated && agg
                    ? `${t("home.videoCount", { count: s.videos })} · ${t("home.countriesCount", { count: s.countries })}`
                    : "\u00a0"}
                </div>
              </div>
            </Link>
          );
        })}
        <SuggestYoutuberDialog
          trigger={
            <button
              type="button"
              className="text-left group flex flex-col"
            >
              <div className="aspect-square ring-hairline border border-dashed border-ink/25 mb-1.5 grid place-items-center bg-secondary/40 group-hover:border-amber transition-colors">
                <span className="text-2xl text-ink/40 group-hover:text-amber transition-colors">+</span>
              </div>
              <span className="font-serif text-xs leading-tight group-hover:text-amber transition-colors">
                {t("home.suggest.cta")}
              </span>
            </button>
          }
        />
      </div>
    </section>
  );
}



function RecentVideos() {
  const { t, i18n } = useTranslation();
  const { data: videos } = useSuspenseQuery(recentVideosQuery);
  const [sort, setSortState] = useState<"recent" | "views">("recent");
  const [page, setPage] = useState(0);
  const PAGE = 10;
  const setSort = (s: "recent" | "views") => {
    setSortState(s);
    setPage(0);
  };
  // The full catalogue is only needed past the first page or for "most viewed".
  const needAll = sort === "views" || page > 0;
  const { data: allData, isFetching } = useQuery({ ...allVideosQuery, enabled: needAll });

  const withLoc = useMemo(() => {
    if (!needAll || !allData) return videos.filter((v) => primaryLocation(v));
    const list = allData.filter((v) => primaryLocation(v)).slice();
    if (sort === "views") list.sort((a, b) => (b.view_count ?? 0) - (a.view_count ?? 0));
    else
      list.sort(
        (a, b) => new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime(),
      );
    return list;
  }, [sort, videos, allData, needAll]);
  const waiting = needAll && !allData;
  const pageItems = waiting ? [] : withLoc.slice(page * PAGE, page * PAGE + PAGE);
  const hasNext = waiting || !allData ? true : (page + 1) * PAGE < withLoc.length;
  const go = (p: number) => {
    setPage(p);
    document.getElementById("home-videos")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (withLoc.length === 0) return null;
  return (
    <section id="home-videos" className="py-10 border-t border-ink/8 scroll-mt-4">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-5">
          <button
            type="button"
            onClick={() => setSort("recent")}
            className={`font-serif text-xl md:text-2xl transition-colors ${
              sort === "recent" ? "text-ink border-b-2 border-amber" : "text-ink/35 hover:text-ink/60"
            }`}
          >
            {t("home.recent")}
          </button>
          <button
            type="button"
            onClick={() => setSort("views")}
            className={`font-serif text-xl md:text-2xl transition-colors ${
              sort === "views" ? "text-ink border-b-2 border-amber" : "text-ink/35 hover:text-ink/60"
            }`}
          >
            {t("home.mostViewed")}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {waiting && (
            <div className="col-span-full py-16 text-center text-mono-caps text-ink/40">…</div>
          )}
          {pageItems.map((v) => {
            const loc = primaryLocation(v);
            return (
              <Link
                key={v.id}
                to="/v/$slug"
                params={{ slug: videoParam(v) }}
                className="group bg-paper ring-hairline overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="aspect-video bg-secondary overflow-hidden">
                  {v.thumbnail_url && (
                    <img
                      src={v.thumbnail_url.replace(/\/(maxresdefault|sddefault|hqdefault)\.jpg/, "/mqdefault.jpg")}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      width={320}
                      height={180}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                    />
                  )}
                </div>
                <div className="p-2.5">
                  <h4 className="font-serif text-xs leading-snug line-clamp-2 group-hover:text-amber transition-colors mb-1.5">
                    {cleanTitle(v.title)}
                  </h4>
                  <div className="flex items-center justify-between text-[10px] font-mono text-ink/50">
                    <span className="truncate">{v.youtubers?.name}</span>
                    <span className="whitespace-nowrap">{formatDate(v.published_at, i18n.language)}</span>
                  </div>
                  {sort === "views" && (
                    <div className="mt-1 text-[10px] font-mono text-ink/70">
                      {formatCompact(v.view_count ?? 0)} {t("home.views")}
                    </div>
                  )}
                  {loc?.country_code && (
                    <div className="mt-1">
                      <CountryLabel
                        code={loc.country_code}
                        name={loc.country_name}
                        className="text-[10px] text-amber"
                        size={20}
                      />
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => go(page - 1)}
            className="ring-hairline bg-paper text-sm px-3.5 py-1.5 hover:bg-secondary transition-colors disabled:opacity-30 disabled:pointer-events-none"
          >
            ← {t("home.prevPage")}
          </button>
          <span className="text-mono-caps text-ink/50">{page + 1}</span>
          <button
            type="button"
            disabled={!hasNext || isFetching && waiting}
            onClick={() => go(page + 1)}
            className="bg-ink text-paper text-sm font-medium px-3.5 py-1.5 hover:bg-amber transition-colors disabled:opacity-30 disabled:pointer-events-none"
          >
            {t("home.nextPage")} →
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * "The archive in numbers" band. The figures are server-rendered from the
 * database, then recomputed from the public archive once the browser has it.
 */
function ArchiveStats() {
  const { t, i18n } = useTranslation();
  const { data: stats } = useQuery(siteStatsQuery);
  if (!stats) return null;

  const items = [
    { value: stats.videos, label: t("about.statVideos") },
    { value: stats.locations, label: t("about.statLocations") },
    { value: stats.youtubers, label: t("about.statChannels") },
    { value: stats.countries, label: t("about.statCountries") },
  ];

  return (
    <section className="max-w-7xl mx-auto px-6 pb-6">
      <div className="ring-hairline bg-paper/60 px-4 py-3 md:px-6 md:py-4 flex flex-wrap items-center gap-x-6 gap-y-2 md:gap-x-10">
        {items.map((it) => (
          <div key={it.label} className="flex items-baseline gap-2">
            <span className="text-lg md:text-2xl tabular-nums leading-none">
              {formatNumber(it.value, i18n.language)}
            </span>
            <span className="text-mono-caps text-ink/50">{it.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
